import { Css, SelectField, useTestIds } from "@homebound/beam";

/** One field of the row being reviewed, and the pages it cites. */
export type AiSource = { id: string; label: string; pageNumbers: number[] };

type AiSourceViewerHeaderProps = {
  sources: AiSource[];
  selected: AiSource | undefined;
  onSelect: (source: AiSource) => void;
  currentPage: number;
  onSelectPage: (page: number) => void;
};

/** The one piece of chrome that is ours rather than the document's: what we're citing, and where. */
export function AiSourceViewerHeader(props: AiSourceViewerHeaderProps) {
  const { sources, selected, onSelect, currentPage, onSelectPage } = props;
  const tid = useTestIds(props, "aiSourceViewerHeader");
  const pages = selected?.pageNumbers ?? [];

  return (
    <header css={Css.df.aic.jcc.gap3.fs0.hPx(60).px3.bgWhite.bb.bcGray200.$} {...tid}>
      <div css={Css.wPx(320).$}>
        {/* Each source already reads `Field: value`, so the field needs no label of its own. */}
        <SelectField
          label="Source"
          labelStyle="hidden"
          options={sources}
          getOptionLabel={(s) => s.label}
          getOptionValue={(s) => s.id}
          value={selected?.id}
          onSelect={(_id, source) => source && onSelect(source)}
          // The backend creates a row's proposals in the tab's own field order; alphabetical would scramble it.
          autoSort={false}
          {...tid.source}
        />
      </div>
      {pages.nonEmpty ? (
        <>
          <span css={Css.smSb.$} {...tid.foundOn}>
            Found on {pages.length} {pages.length === 1 ? "Page" : "Pages"}
          </span>
          {pages.map((page) => (
            <button
              key={page}
              type="button"
              aria-current={page === currentPage ? "page" : undefined}
              onClick={() => onSelectPage(page)}
              css={page === currentPage ? Css.smSb.blue800.$ : Css.sm.blue600.$}
              {...tid.page}
            >
              Page {page}
            </button>
          ))}
        </>
      ) : (
        <span css={Css.sm.gray700.$} {...tid.noPages}>
          The import didn't cite any pages here.
        </span>
      )}
    </header>
  );
}
